import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "369",
  "slug": "map-scale-explained",
  "title": "Map Scale Explained: How to Measure Real Distances on Any Map",
  "excerpt": "A map is useless if you can't tell how far apart things really are. Here's how map scale works, the three ways it's written, and how to measure actual distances with confidence.",
  "date": "2026-06-07",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1736117704452-46670dd0c81f?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx0b3BvZ3JhcGhpYyUyMG1hcCUyMGRldGFpbHxlbnwxfDB8fHwxNzgwODQ1NDI2fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "Every map shrinks the real world down to fit on a page or a screen, and map scale is the number that tells you by how much. Get comfortable with scale and a map stops being a vague picture and becomes a precise measuring instrument: you can read off how far it is between two towns, how big a lake is, or whether a hiking route is two kilometres or twenty. It is one of the most useful map skills there is, and the core idea is simpler than it first sounds."
    },
    {
      "type": "heading",
      "text": "What Map Scale Means"
    },
    {
      "type": "paragraph",
      "text": "Scale is just the ratio between a distance on the map and the matching distance on the ground. If one centimetre on the map stands for one hundred thousand centimetres in the real world, that is, one kilometre, then the map has a scale of 1 to 100,000. The ratio holds for any unit you like: one inch on the map represents 100,000 inches on the ground. Scale is what lets you translate the tidy little world on the paper back into the enormous real one outside."
    },
    {
      "type": "heading",
      "text": "The Three Ways Scale Is Written"
    },
    {
      "type": "paragraph",
      "text": "Scale shows up in three forms, and they all say the same thing. A representative fraction is the pure ratio, written like 1:100,000 or 1/100,000. A verbal scale spells it out in words, such as one centimetre represents one kilometre. And a graphic or bar scale is a small ruler printed right on the map, marked off in real-world distances. The bar scale has one big advantage: if the map is photocopied larger or smaller, or zoomed on a screen, the printed bar shrinks and grows along with it and stays correct, while the written ratio does not."
    },
    {
      "type": "heading",
      "text": "Large Scale vs Small Scale"
    },
    {
      "type": "paragraph",
      "text": "Here is the part that trips almost everyone up. A large-scale map shows a small area in lots of detail, like a street map of a single neighbourhood, while a small-scale map shows a large area with little detail, like a map of a whole continent. It feels backwards, but it follows from the fraction: 1/10,000 is a larger number than 1/10,000,000. The bigger the fraction, the larger the scale, and the smaller the area it covers. When in doubt, remember: large scale, large detail, small place."
    },
    {
      "type": "heading",
      "text": "How to Measure a Real Distance"
    },
    {
      "type": "paragraph",
      "text": "Measuring is straightforward once you trust the scale. Lay a ruler between the two points on the map and read off the map distance, say, four centimetres. Then multiply by the scale. On a 1:100,000 map, four centimetres becomes 400,000 centimetres, which is four kilometres on the ground. If the map has a bar scale, it is even easier: mark the map distance on the edge of a slip of paper, hold it against the bar scale, and read the real distance directly. No multiplication required."
    },
    {
      "type": "heading",
      "text": "Why Scale Isn't Perfectly Constant"
    },
    {
      "type": "paragraph",
      "text": "There is one honest complication. The Earth is curved and a map is flat, so flattening the globe always stretches some areas and squeezes others. On a map of a small region the distortion is negligible and the stated scale is reliable everywhere. But on a map of the whole world, scale genuinely varies from place to place, which is why a Mercator world map makes Greenland look as big as Africa even though Africa is many times larger. The smaller the area a map covers, the more you can trust a single scale across all of it."
    },
    {
      "type": "heading",
      "text": "Choosing the Right Scale"
    },
    {
      "type": "paragraph",
      "text": "The best scale depends entirely on the job. A hiker wants a large-scale map that shows individual trails, streams, and contour lines. A road-tripper wants a medium scale that fits a whole region on one sheet. A geographer comparing continents wants a small-scale map of the world. There is no single best scale, only the right scale for what you are trying to see, and knowing how to read whichever one is in front of you."
    }
  ]
};
